/**
 * Redux Hooks
 * 
 * Typed Redux hooks for use throughout the application.
 * These hooks provide type safety when interacting with the Redux store,
 * ensuring proper TypeScript integration and preventing type errors.
 */

import { useDispatch, useSelector } from 'react-redux';
import type { TypedUseSelectorHook } from 'react-redux';
import type { RootState, AppDispatch } from '../store';

/**
 * Typed dispatch hook
 * 
 * Returns the dispatch function with proper TypeScript typing.
 * Use this instead of the plain useDispatch hook to get type safety
 * for all action creators.
 * 
 * @returns Typed dispatch function
 */
export const useAppDispatch = () => useDispatch<AppDispatch>();

/**
 * Typed selector hook
 * 
 * Provides type-safe access to the Redux store state.
 * Use this instead of the plain useSelector hook to get automatic
 * TypeScript completion and type checking for state properties.
 * 
 * @returns Typed selector function
 */
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
